import {
  DeviceType,
  EProjectType,
  ESdChangeReason,
  IDevice,
  IProject,
  ViewPart,
  Visibility
} from "@kuika/kuika-cl-model";
import { IScreenCanvasSize } from "@kuika/kuika-cl-model/src/models/screen-canvas-sizes";
import _ from "lodash";
import { MenuInfo } from "rc-menu/es/interface";
import deviceListJson from "../../../../../shared/data/device-list.json";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import { DeviceMenuDropdown } from "./device-menu-dropdown";

export interface IDeviceMenuState {
  deviceTypes: string[];
  isInitialized: boolean;
}

export interface IDeviceMenuProps {}

class DeviceMenu extends PureSdComponent<IDeviceMenuProps & IWithSDProps, IDeviceMenuState> {
  deviceList: IDevice[] = deviceListJson as unknown as IDevice[];

  constructor(props: IDeviceMenuProps & IWithSDProps) {
    super(props);
    const deviceTypes: string[] = _.keys(
      _.countBy(this.deviceList, (device) => {
        return device.type;
      })
    );
    this.state = { deviceTypes, isInitialized: false };
  }

  componentDidUpdate(prevProps: IDeviceMenuProps & IWithSDProps) {
    if (this.props.sd.project.projectType === undefined) return;
    const isSdChanged = prevProps.sd.sdId !== this.props.sd.sdId;
    if (this.state.isInitialized && !isSdChanged) return;

    const { sd } = this.props;
    const project: IProject = sd.project;
    const { screenCanvasSizes } = sd.project;
    const { selectedDevice } = sd;
    let screens: IScreenCanvasSize[] | undefined = [];
    let screen: IScreenCanvasSize | undefined;

    if (screenCanvasSizes) {
      screens = screenCanvasSizes.screens;
      screen = screens?.find((s) => s.id === this.props.sd.sdId);
    }

    if (!screen || screens?.length === 0) {
      if (project.projectType === EProjectType.Mobile) {
        sd.setDeviceType(DeviceType.Phone);
        this.togglePhone();
        sd.setResolution(480);
      } else {
        this.toggleDesktop();
      }
    } else if (screens && screens.length > 0 && screen) {
      if (screen.deviceType && !screen.device) {
        switch (screen.deviceType) {
          case "Desktop":
            this.toggleDesktop();
            break;
          case "Tablet":
            this.toggleTablet();
            break;
          case "Phone":
            this.togglePhone();
            break;
          default:
            break;
        }
      } else if (screen?.device) {
        const device = this.deviceList.find((d) => d.id === screen?.device?.id);
        if (device) {
          this.toggleDeviceType(device.type, device.id, true);
        }
      }
    } else if (screenCanvasSizes.deviceType && !screenCanvasSizes.device) {
      if (project.projectType === EProjectType.Mobile) {
        sd.setDeviceType(DeviceType.Phone);
        this.togglePhone(true);
        sd.setResolution(480);
      } else {
        switch (screenCanvasSizes.deviceType) {
          case "Desktop":
            this.toggleDesktop(true);
            break;
          case "Tablet":
            this.toggleTablet();
            break;
          case "Phone":
            this.togglePhone();
            break;
          default:
            break;
        }
      }
    } else if (screenCanvasSizes.device) {
      const device = this.deviceList.find((d) => d.id === screenCanvasSizes.device?.id);
      if (device) {
        this.toggleDeviceType(device.type, device.id);
      }
    }

    this.setState({ isInitialized: true });
  }

  handleSelectDevice = (param: MenuInfo) => {
    this.selectDevice(param.key);
  };

  isSelectedDeviceType(type: string): boolean {
    const selectedDevice: IDevice = this.getSelectedDevice();
    return selectedDevice.type === type;
  }

  toggleDeviceType = (type: string, defaultDeviceKey: string, isInitialWithScreenSettings?: boolean) => {
    if (!this.isSelectedDeviceType(type) || isInitialWithScreenSettings) {
      this.selectDevice(defaultDeviceKey, isInitialWithScreenSettings);
    }
    // this.selectDevice("none");
  };

  togglePhone = (isInit?: boolean) => {
    this.toggleDeviceType("Phone", "iPhoneX", isInit);
  };

  toggleTablet = () => {
    this.toggleDeviceType("Tablet", "iPadPro");
  };

  toggleDesktop = (isInit?: boolean) => {
    this.toggleDeviceType("Desktop", "sxga", isInit);
  };

  selectDevice = (key: string, isInitialWithScreenSettings?: boolean) => {
    if (this.deviceList?.length <= 0) return;

    const devices = this.deviceList.filter((device: IDevice) => device.id === key);

    if (devices.length > 0 && this.props.sd.setSpecificDevice) {
      this.props.sd.setSpecificDevice(devices[0], true);
      this.setScreenCanvasWidth(
        parseInt(devices[0].viewport.substring(0, devices[0].viewport.indexOf("x"))) + 24,
        devices[0],
        isInitialWithScreenSettings
      );
    }
  };

  setScreenCanvasWidth = (width: number, device: IDevice, isInitialWithScreenSettings?: boolean) => {
    // Restoring the device the record already stores must not write it back: the previous
    // version mutated the project in place and then skipped the save, so memory and server
    // disagreed for the rest of the session.
    if (isInitialWithScreenSettings) {
      return;
    }
    // A report is sized by its paper. This menu is not mounted in the Report Designer, but the
    // tool switches to UI Design before the screen does, so on the way out the report is still
    // the loaded screen and used to have its size cleared here.
    if (this.props.sd.isLoadedScreenReport?.() === true) {
      return;
    }
    const {
      sd: { project, updateProject }
    } = this.props;
    let { screenCanvasSizes } = project;
    if (!screenCanvasSizes) {
      screenCanvasSizes = {
        width: undefined,
        deviceType: "Desktop"
      };
    }
    const screen = screenCanvasSizes?.screens?.find((s) => s.id === this.props.sd.sdId);
    if (width && (screenCanvasSizes.width !== width || (screen && screen.width !== width))) {
      const canvasSizeByScreen: IScreenCanvasSize = {
        id: this.props.sd.sdId,
        width: undefined,
        deviceType: device.type,
        device
      };

      if (!screenCanvasSizes?.screens) screenCanvasSizes.screens = [];
      if (screen) {
        screen.width = undefined;
        screen.device = device;
        screen.deviceType = device.type;
      } else {
        screenCanvasSizes.screens.push(canvasSizeByScreen);
      }
      updateProject({ ...project, screenCanvasSizes });
    }
  };

  getSelectedDevice = (): IDevice => {
    return this.props && this.props.sd && this.props.sd.selectedDevice
      ? this.props.sd.selectedDevice
      : this.deviceList[0];
  };

  render() {
    return <>{this.props.sd.getVisibility(ViewPart.Devices) === Visibility.Visible && <DeviceMenuDropdown />}</>;
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const deviceMenu = withSD<IDeviceMenuProps>(DeviceMenu);
export { deviceMenu as DeviceMenu };
